import { ArrowLeftRight, Crosshair, MapPin, Trash2, Video } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ModelViewerCanvas,
  type ModelViewerHandle,
  type SurfaceHit,
} from '@/components/canvas/elements/ModelViewerCanvas';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Section } from '@/components/ui/Section';
import type { CameraPose } from 'ai-tutor-player';
import type { CameraKeyframe, Hotspot3DLabel, RevealStep, ThreeDContent } from '@/lib/schema.types';
import { uid } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

interface ThreeDTarget {
  id: string;
  label: string;
  src: string;
  poster?: string;
  defaultPose: CameraPose;
}

function asThreeD(content: unknown): ThreeDContent {
  if (typeof content === 'string') return { src: content };
  if (content && typeof content === 'object') return content as ThreeDContent;
  return { src: '' };
}

/**
 * Authors camera keyframes for the 3D model(s) on this slide, timed against the
 * selected reveal step's narration. Orbit the mini-viewer to a view, optionally
 * drop a 3D label, set the trigger time, and capture — the keyframe joins the
 * step and is replayed by the preview clock (and, via the documented contract,
 * the Electron classroom). Mirrors the annotations-list pattern in StepInspector.
 */
export function CameraKeyframeEditor({ slideId, step }: { slideId: string; step: RevealStep | null }) {
  const actions = useEditorActions();
  const slide = useEditorStore((s) => s.script?.slides.find((x) => x.id === slideId) ?? null);

  const targets = useMemo<ThreeDTarget[]>(() => {
    const out: ThreeDTarget[] = [];
    slide?.rows.forEach((row) =>
      row.columns.forEach((col) =>
        col.elements.forEach((el) => {
          if (el.type !== '3d') return;
          const data = asThreeD(el.content);
          if (!data.src) return;
          out.push({
            id: el.id,
            label: data.alt || `3D model (${el.id.slice(-4)})`,
            src: data.src,
            poster: data.poster,
            defaultPose: {
              cameraOrbit: data.defaultCameraOrbit ?? '0deg 75deg 105%',
              cameraTarget: data.defaultCameraTarget ?? 'auto auto auto',
              fieldOfView: data.fieldOfView,
            },
          });
        }),
      ),
    );
    return out;
  }, [slide]);

  const viewerRef = useRef<ModelViewerHandle>(null);
  const [targetId, setTargetId] = useState<string | null>(targets[0]?.id ?? null);
  const [triggerSec, setTriggerSec] = useState(0);
  const [labelText, setLabelText] = useState('');
  const [placing, setPlacing] = useState(false);
  const [pendingLabel, setPendingLabel] = useState<SurfaceHit | null>(null);
  const [labelSize, setLabelSize] = useState(13);
  const [labelAnim, setLabelAnim] = useState<'fade' | 'glitch'>('fade');
  const [labelSide, setLabelSide] = useState<'left' | 'right'>('right');
  // Ease duration for every camera move on this element (the speed between
  // labelled views). Seeded from existing keyframes when present.
  const [moveMs, setMoveMs] = useState(
    () => (step?.cameraKeyframes ?? []).find((k) => k.transitionMs != null)?.transitionMs ?? 300,
  );

  // While placing a label, Esc should cancel placing — NOT close Teach mode.
  // Capture phase + stopImmediatePropagation pre-empts TeachStudio's window
  // Esc handler (which calls onClose).
  useEffect(() => {
    if (!placing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setPlacing(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [placing]);

  if (targets.length === 0) return null;

  const target = targets.find((t) => t.id === targetId) ?? targets[0];
  const allKeyframes = step?.cameraKeyframes ?? [];
  const keyframes = allKeyframes
    .filter((k) => k.targetElementId === target.id)
    .slice()
    .sort((a, b) => (a.triggerMs ?? 0) - (b.triggerMs ?? 0));

  const writeKeyframes = (nextForTarget: CameraKeyframe[]) => {
    if (!step) return;
    const others = allKeyframes.filter((k) => k.targetElementId !== target.id);
    actions.upsertRevealStep(slideId, { ...step, cameraKeyframes: [...others, ...nextForTarget] });
  };

  const durationSec = (step?.audio?.durationMs ?? 0) / 1000;

  const captureKeyframe = () => {
    const pose = viewerRef.current?.capturePose();
    if (!pose || !step) return;
    const kf: CameraKeyframe = {
      id: uid('kf'),
      targetElementId: target.id,
      triggerMs: Math.max(0, Math.round(triggerSec * 1000)),
      cameraOrbit: pose.cameraOrbit,
      cameraTarget: pose.cameraTarget,
      fieldOfView: pose.fieldOfView,
      transitionMs: moveMs,
      labels:
        labelText.trim() && pendingLabel
          ? [
              {
                id: uid('lbl'),
                text: labelText.trim(),
                position: pendingLabel.position,
                normal: pendingLabel.normal,
                fontSize: labelSize,
                animation: labelAnim,
                side: labelSide,
              },
            ]
          : undefined,
    };
    writeKeyframes([...keyframes, kf]);
    setLabelText('');
    setPendingLabel(null);
    setPlacing(false);
  };

  const updateTrigger = (id: string, sec: number) => {
    writeKeyframes(
      keyframes.map((k) =>
        k.id === id ? { ...k, triggerMs: Math.max(0, Math.round(sec * 1000)) } : k,
      ),
    );
  };

  const remove = (id: string) => writeKeyframes(keyframes.filter((k) => k.id !== id));

  // One speed for the whole element — apply to every existing keyframe + use it
  // for new captures.
  const setSpeed = (ms: number) => {
    setMoveMs(ms);
    if (keyframes.length > 0) writeKeyframes(keyframes.map((k) => ({ ...k, transitionMs: ms })));
  };

  // Patch the first label of a keyframe (side / exit time live on the label).
  const patchLabel = (kfId: string, patch: Partial<Hotspot3DLabel>) => {
    writeKeyframes(
      keyframes.map((k) =>
        k.id === kfId && k.labels && k.labels.length > 0
          ? { ...k, labels: [{ ...k.labels[0], ...patch }, ...k.labels.slice(1)] }
          : k,
      ),
    );
  };

  if (!step) {
    return (
      <Section title="Camera keyframes">
        <p className="text-[11px] text-fg-subtle">Select a reveal step to add camera moves.</p>
      </Section>
    );
  }

  return (
    <Section title={`Camera keyframes (${keyframes.length})`}>
      {targets.length > 1 && (
        <select
          value={target.id}
          onChange={(e) => setTargetId(e.target.value)}
          className="mb-2 h-8 w-full rounded-md border border-border bg-white px-2 text-[12px] text-fg"
        >
          {targets.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      )}

      <div className="mb-2">
        <span className="mb-1 flex items-center justify-between text-[11px] font-medium text-fg-muted">
          <span>Camera move speed</span>
          <span className="text-fg-subtle">{(moveMs / 1000).toFixed(1)}s / move</span>
        </span>
        <input
          type="range"
          min="200"
          max="2600"
          step="100"
          value={2800 - moveMs}
          onChange={(e) => setSpeed(2800 - Number(e.target.value))}
          className="w-full"
          title="How fast the camera eases between labelled views (right = faster)"
        />
      </div>

      <p className="mb-1.5 text-[10.5px] text-fg-subtle">
        Orbit to the view you want, set when it fires in the narration, then capture. The model
        centers itself whenever no label is showing.
      </p>

      {placing && (
        <p className="mb-1 rounded bg-accent-soft px-2 py-1 text-[11px] font-medium text-accent">
          👆 Click the spot on the model below to anchor “{labelText.trim()}”.
        </p>
      )}

      <div
        className={`relative h-44 w-full overflow-hidden rounded-md bg-gradient-to-br from-slate-100 to-slate-200 ${
          placing ? 'cursor-crosshair ring-2 ring-accent' : ''
        }`}
      >
        <ModelViewerCanvas
          ref={viewerRef}
          src={target.src}
          poster={target.poster}
          cameraControls={!placing}
          labels={
            pendingLabel && labelText.trim()
              ? [
                  {
                    id: 'pending',
                    text: labelText.trim(),
                    position: pendingLabel.position,
                    normal: pendingLabel.normal,
                    fontSize: labelSize,
                    animation: labelAnim,
                  },
                ]
              : undefined
          }
          onModelClick={placing ? (hit) => setPendingLabel(hit) : undefined}
          className="h-full w-full"
          style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
        />
        <button
          type="button"
          onClick={() => viewerRef.current?.recenter()}
          title="Recenter model"
          className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-md border border-border bg-white/90 px-2 py-1 text-[10.5px] font-medium text-fg-muted shadow-sm hover:text-fg"
        >
          <Crosshair size={12} /> Recenter
        </button>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium text-fg-muted">Trigger (s)</span>
          <input
            type="number"
            min="0"
            step="0.1"
            value={triggerSec}
            onChange={(e) => setTriggerSec(Number(e.target.value) || 0)}
            className="h-8 w-full rounded-md border border-border bg-white px-2 text-[12px] text-fg"
          />
        </label>
        <div className="flex items-end">
          <span className="text-[10.5px] text-fg-subtle">
            {durationSec > 0 ? `Narration ≈ ${durationSec.toFixed(1)}s` : 'No audio yet'}
          </span>
        </div>
      </div>

      <div className="mt-2">
        <Input
          label="Label (optional)"
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
          placeholder="e.g. Left atrium"
        />
        {labelText.trim() && (
          <>
            <button
              type="button"
              onClick={() => setPlacing((p) => !p)}
              className={`mt-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] ${
                placing
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border text-fg-muted hover:text-fg'
              }`}
            >
              <MapPin size={12} />
              {pendingLabel
                ? 'Label placed ✓ — click to move'
                : placing
                  ? 'Click the model…'
                  : 'Place on model'}
            </button>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-fg-muted">
                  Size ({labelSize}px)
                </span>
                <input
                  type="range"
                  min="9"
                  max="28"
                  step="1"
                  value={labelSize}
                  onChange={(e) => setLabelSize(Number(e.target.value) || 13)}
                  className="w-full"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-fg-muted">Animation</span>
                <select
                  value={labelAnim}
                  onChange={(e) => setLabelAnim(e.target.value as 'fade' | 'glitch')}
                  className="h-8 w-full rounded-md border border-border bg-white px-2 text-[12px] text-fg"
                >
                  <option value="fade">Fade in / out</option>
                  <option value="glitch">Glitch in / out</option>
                </select>
              </label>
            </div>
            <button
              type="button"
              onClick={() => setLabelSide((s) => (s === 'left' ? 'right' : 'left'))}
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-fg-muted hover:text-fg"
            >
              <ArrowLeftRight size={12} /> Side: {labelSide === 'left' ? 'Left' : 'Right'}
            </button>
          </>
        )}
      </div>

      <Button size="sm" variant="primary" onClick={captureKeyframe} className="mt-2 w-full">
        <Video size={13} /> Capture keyframe
      </Button>

      {keyframes.length > 0 && (
        <ul className="mt-3 space-y-1">
          {keyframes.map((k) => {
            const label = k.labels?.[0];
            return (
              <li
                key={k.id}
                className="rounded-md border border-border px-2 py-1 text-[12px]"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={((k.triggerMs ?? 0) / 1000).toString()}
                    onChange={(e) => updateTrigger(k.id, Number(e.target.value) || 0)}
                    className="h-6 w-14 rounded border border-border px-1 text-[11px]"
                    title="Enter time (s)"
                  />
                  <span className="text-fg-subtle">s in</span>
                  <span className="flex-1 truncate" title={label?.text}>
                    {label?.text ?? 'camera move'}
                  </span>
                  <button
                    onClick={() => remove(k.id)}
                    className="text-fg-subtle hover:text-red-500"
                    title="Remove keyframe"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {label && (
                  <div className="mt-1 flex items-center gap-2 pl-1 text-[11px] text-fg-muted">
                    <span>exit</span>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="auto"
                      value={label.endMs != null ? (label.endMs / 1000).toString() : ''}
                      onChange={(e) =>
                        patchLabel(k.id, {
                          endMs:
                            e.target.value === ''
                              ? undefined
                              : Math.max(0, Math.round(Number(e.target.value) * 1000)),
                        })
                      }
                      className="h-6 w-14 rounded border border-border px-1 text-[11px]"
                      title="Label exit time (s) — blank = with the camera move"
                    />
                    <span>s</span>
                    <button
                      onClick={() =>
                        patchLabel(k.id, { side: label.side === 'left' ? 'right' : 'left' })
                      }
                      className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 hover:text-fg"
                      title="Swap label side"
                    >
                      <ArrowLeftRight size={11} /> {label.side === 'left' ? 'Left' : 'Right'}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
